import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CategoryModal from './services/CategoryModal';
import ServiceModal, { generateSlug } from './services/ServiceModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('adminToken')}`,
});

const emptyServiceForm = (defaultCategory = '') => ({
  title_ru: '',
  title_en: '',
  description_ru: '',
  description_en: '',
  timeframe_ru: '',
  timeframe_en: '',
  price_ru: '',
  price_en: '',
  preview_image: '',
  category: defaultCategory,
  slug: '',
  is_active: true,
  full_content_ru: '',
  full_content_en: '',
  faq_ru: [],
  faq_en: [],
  work_complex_ru: [],
  work_complex_en: [],
  required_docs_ru: [],
  required_docs_en: [],
  seo_title_ru: '',
  seo_title_en: '',
  seo_description_ru: '',
  seo_description_en: '',
  seo_keywords_ru: '',
  seo_keywords_en: '',
  seo_variants_ru: [],
  seo_variants_en: [],
  seo_keywords_extra_ru: [],
  seo_keywords_extra_en: [],
});

const ServicesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');
  const [notification, setNotification] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    key: '',
    name_ru: '',
    name_en: '',
    icon: 'FileText',
    color: 'gray',
    order: 0,
    is_active: true,
  });

  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState(emptyServiceForm());

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, servRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/service-categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/services`, { headers: getAuthHeaders() }),
      ]);
      setCategories(catRes.data);
      setServices(servRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Category handlers
  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        key: category.key,
        name_ru: category.name_ru,
        name_en: category.name_en,
        icon: category.icon || 'FileText',
        color: category.color || 'gray',
        order: category.order || 0,
        is_active: category.is_active !== false,
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        key: '',
        name_ru: '',
        name_en: '',
        icon: 'FileText',
        color: 'gray',
        order: categories.length + 1,
        is_active: true,
      });
    }
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(
          `${API_URL}/api/admin/service-categories/${editingCategory.id}`,
          categoryForm,
          { headers: getAuthHeaders() },
        );
        showNotification('Категория обновлена');
      } else {
        await axios.post(
          `${API_URL}/api/admin/service-categories`,
          categoryForm,
          { headers: getAuthHeaders() },
        );
        showNotification('Категория создана');
      }
      setShowCategoryModal(false);
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Ошибка сохранения', 'error');
    }
  };

  const handleDeleteCategory = async (category) => {
    const count = services.filter((s) => s.category === category.key).length;
    if (count > 0) {
      showNotification(`Нельзя удалить: ${count} услуг в категории`, 'error');
      return;
    }
    if (!window.confirm(`Удалить категорию "${category.name_ru}"?`)) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/service-categories/${category.id}`,
        { headers: getAuthHeaders() },
      );
      showNotification('Категория удалена');
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Ошибка удаления', 'error');
    }
  };

  // Service handlers
  const openServiceModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        ...emptyServiceForm(),
        ...Object.fromEntries(
          Object.entries(emptyServiceForm()).map(([k, v]) => [k, service[k] ?? v]),
        ),
        is_active: service.is_active !== false,
      });
    } else {
      setEditingService(null);
      setServiceForm(emptyServiceForm(categories[0]?.key || ''));
    }
    setShowServiceModal(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await axios.put(
          `${API_URL}/api/admin/services/${editingService.id}`,
          serviceForm,
          { headers: getAuthHeaders() },
        );
        showNotification('Услуга обновлена');
      } else {
        await axios.post(
          `${API_URL}/api/admin/services`,
          serviceForm,
          { headers: getAuthHeaders() },
        );
        showNotification('Услуга создана');
      }
      setShowServiceModal(false);
      loadData();
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Ошибка сохранения', 'error');
    }
  };

  const handleDeleteService = async (service) => {
    if (!window.confirm(`Удалить услугу "${service.title_ru}"?`)) return;

    try {
      await axios.delete(
        `${API_URL}/api/admin/services/${service.id}`,
        { headers: getAuthHeaders() },
      );
      showNotification('Услуга удалена');
      loadData();
    } catch (error) {
      showNotification('Ошибка удаления', 'error');
    }
  };

  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter((s) => s.category === selectedCategory);

  const getCategoryName = (key) => {
    const cat = categories.find((c) => c.key === key);
    return cat?.name_ru || key;
  };

  if (loading) {
    return <div className="p-6 text-center" data-testid="services-loading">Загрузка...</div>;
  }

  return (
    <div className="space-y-6" data-testid="services-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление услугами</h1>
          <p className="text-gray-600 mt-1">Категории: {categories.length} | Услуги: {services.length}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => openCategoryModal()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition"
            data-testid="create-category-btn"
          >
            + Создать категорию
          </button>
          <button
            onClick={() => openServiceModal()}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-medium transition"
            data-testid="create-service-btn"
          >
            + Добавить услугу
          </button>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`p-4 rounded-lg ${
            notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
          data-testid="services-notification"
        >
          {notification.message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('services')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'services'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="services-tab-btn"
          >
            📋 Услуги ({services.length})
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categories'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="categories-tab-btn"
          >
            🏷️ Категории ({categories.length})
          </button>
        </nav>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Порядок</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ключ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Название</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Услуг</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((cat) => {
                const servicesCount = services.filter((s) => s.category === cat.key).length;
                return (
                  <tr key={cat.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{cat.order}</td>
                    <td className="px-6 py-4">
                      <code className="px-2 py-1 bg-gray-100 rounded text-sm">{cat.key}</code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{cat.name_ru}</div>
                      <div className="text-xs text-gray-500">{cat.name_en}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 rounded-full">
                        {servicesCount}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        cat.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {cat.is_active ? 'Активна' : 'Скрыта'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => openCategoryModal(cat)}
                        className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                        data-testid={`edit-category-${cat.key}`}
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat)}
                        className="text-red-600 hover:text-red-900 font-medium text-sm"
                        disabled={servicesCount > 0}
                        data-testid={`delete-category-${cat.key}`}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                selectedCategory === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-category-all"
            >
              Все ({services.length})
            </button>
            {categories.map((cat) => {
              const count = services.filter((s) => s.category === cat.key).length;
              return (
                <button
                  key={cat.key}
                  onClick={() => setSelectedCategory(cat.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    selectedCategory === cat.key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-category-${cat.key}`}
                >
                  {cat.name_ru} ({count})
                </button>
              );
            })}
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Услуга</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Категория</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Сроки</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Цена</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServices.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {service.preview_image && (
                          <img src={service.preview_image} alt="" className="w-10 h-10 object-cover rounded mr-3" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{service.title_ru}</div>
                          <div className="text-xs text-gray-500">{service.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">
                        {getCategoryName(service.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{service.timeframe_ru}</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">{service.price_ru || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        service.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {service.is_active ? 'Активна' : 'Скрыта'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => openServiceModal(service)}
                        className="text-orange-600 hover:text-orange-900 font-medium text-sm"
                        data-testid={`edit-service-${service.slug}`}
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeleteService(service)}
                        className="text-red-600 hover:text-red-900 font-medium text-sm"
                        data-testid={`delete-service-${service.slug}`}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <CategoryModal
        show={showCategoryModal}
        editingCategory={editingCategory}
        categoryForm={categoryForm}
        setCategoryForm={setCategoryForm}
        onSubmit={handleCategorySubmit}
        onClose={() => setShowCategoryModal(false)}
      />

      <ServiceModal
        show={showServiceModal}
        editingService={editingService}
        serviceForm={serviceForm}
        setServiceForm={setServiceForm}
        categories={categories}
        onSubmit={handleServiceSubmit}
        onClose={() => setShowServiceModal(false)}
      />

      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Синхронизация</h4>
        <p className="text-sm text-blue-800">
          Категории и услуги автоматически синхронизируются между главной страницей и страницей "Услуги".
          Изменения отображаются сразу после сохранения.
        </p>
      </div>
    </div>
  );
};

export { generateSlug };
export default ServicesManagement;
